import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { ROUTE_STATIONS } from "@/lib/site";
import { Reveal, Eyebrow } from "@/components/Reveal";

const HUB = { x: 330, y: 215, n: "Bremen" };
const CITIES = [
  { x: 372, y: 178, n: "Hamburg" },
  { x: 455, y: 200, n: "Berlin" },
  { x: 258, y: 228, n: "Amsterdam" },
  { x: 240, y: 272, n: "Brüssel" },
  { x: 214, y: 332, n: "Paris" },
  { x: 322, y: 392, n: "Zürich" },
  { x: 392, y: 342, n: "München" },
  { x: 490, y: 330, n: "Wien" },
  { x: 388, y: 122, n: "Kopenhagen" },
  { x: 556, y: 206, n: "Warschau" },
  { x: 452, y: 266, n: "Prag" },
  { x: 336, y: 442, n: "Mailand" },
  { x: 392, y: 500, n: "Rom" },
  { x: 452, y: 84, n: "Stockholm" },
  { x: 118, y: 462, n: "Madrid" },
];

const DOTS = [];
for (let x = 40; x <= 640; x += 24) {
  for (let y = 50; y <= 550; y += 24) DOTS.push([x, y]);
}

export const Areas = () => (
  <section id="strecke" data-testid="areas-section" className="relative w-full bg-[#0a0a0a] grain py-20 sm:py-24 lg:py-32 overflow-hidden">
    <div className="w-full px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
      <div className="lg:col-span-5">
        <Reveal>
          <Eyebrow chapter="07">Einsatzgebiete</Eyebrow>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display uppercase font-black leading-[0.9] tracking-tight text-white text-[clamp(2.1rem,7vw,4.4rem)]">
            Die Strecke ist<br />
            <span className="text-[#f95700]">unser Zuhause.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-[15px] sm:text-base text-neutral-400 leading-relaxed max-w-lg">
            Unser Ausgangspunkt ist Bremen. Von dort fahren wir Transporte und Umzüge in ganz
            Deutschland und über die Grenze in die europäischen Nachbarländer.
          </p>
        </Reveal>

        <div className="mt-8 space-y-px bg-white/10">
          {ROUTE_STATIONS.map((a, i) => (
            <Reveal key={a.k} delay={0.1 * i} className="group bg-[#141414] p-6 sm:p-7 flex gap-4 sm:gap-5">
              <span className="font-mono-t text-xs text-[#f95700] pt-1.5">0{i + 1}</span>
              <div>
                <h3 className="font-display text-xl sm:text-2xl font-bold uppercase text-white group-hover:text-[#f95700] transition-colors duration-500">
                  {a.k}
                </h3>
                <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{a.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal delay={0.12} className="lg:col-span-7">
        <div className="relative border border-white/10 bg-[#0d0d0d] p-2">
          <svg viewBox="0 0 680 580" className="w-full h-auto" data-testid="europe-map">
            {DOTS.map(([x, y]) => (
              <circle key={`${x}-${y}`} cx={x} cy={y} r="1.1" fill="rgba(255,255,255,0.12)" />
            ))}

            {CITIES.map((c, i) => (
              <motion.line
                key={c.n}
                x1={HUB.x}
                y1={HUB.y}
                x2={c.x}
                y2={c.y}
                stroke="#f95700"
                strokeWidth="1.2"
                className="route-dash"
                initial={{ opacity: 0, x2: HUB.x, y2: HUB.y }}
                whileInView={{ opacity: 0.8, x2: c.x, y2: c.y }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, delay: 0.2 + i * 0.09, ease: [0.16, 1, 0.3, 1] }}
              />
            ))}

            {CITIES.map((c, i) => (
              <motion.g
                key={`n-${c.n}`}
                initial={{ opacity: 0, scale: 0.4 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.7 + i * 0.07 }}
              >
                <circle cx={c.x} cy={c.y} r="4" fill="#ffffff" />
                <text x={c.x + 9} y={c.y + 4} fill="rgba(255,255,255,0.72)" fontSize="11" fontFamily="IBM Plex Mono, monospace">
                  {c.n}
                </text>
              </motion.g>
            ))}

            <motion.circle
              cx={HUB.x}
              cy={HUB.y}
              r={14}
              fill="#f95700"
              style={{ transformOrigin: `${HUB.x}px ${HUB.y}px` }}
              animate={{ scale: [1, 2.2, 1], opacity: [0.3, 0, 0.3] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "easeOut" }}
            />
            <circle cx={HUB.x} cy={HUB.y} r="8" fill="#f95700" />
            <circle cx={HUB.x} cy={HUB.y} r="8" fill="none" stroke="#fff" strokeWidth="2" />
            <text x={HUB.x + 14} y={HUB.y - 8} fill="#fff" fontSize="17" fontWeight="800" fontFamily="Barlow Condensed, sans-serif" letterSpacing="1">
              BREMEN · HUB
            </text>
          </svg>

          <div className="absolute bottom-3 right-3 flex items-center gap-2 bg-black/70 backdrop-blur px-3 py-2">
            <MapPin size={13} className="text-[#f95700]" />
            <span className="font-mono-t text-[9px] sm:text-[10px] uppercase tracking-widest text-neutral-300">
              Große Weidestraße 2 · 28195 Bremen
            </span>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
